import { deserialize, fetchWrapper } from '../fetch';
import type { SelfServiceClaimFile } from '../files/interface';
import type { Claim, Participant } from './interface';

export const fetchClaims = async (data: {
  filters: string;
  sort?: {
    sort_by: string[] | string;
    is_descending: boolean;
  };
  offset?: number;
  limit?: number;
}): Promise<{ dossiers: Claim[]; count: number }> => {
  const url = new URL('/app/v1/dossiers/paginated', API_BASE_URL);

  url.searchParams.set('filters', data.filters);

  if (data.sort) {
    const sortParams = JSON.stringify({
      sort_by: data.sort.sort_by,
      is_descending: data.sort.is_descending,
    });
    url.searchParams.set('sort_parameters', sortParams);
  }

  if (data.offset) {
    url.searchParams.set('offset', `${data.offset}`);
  }

  if (data.limit) {
    url.searchParams.set('limit', `${data.limit}`);
  }

  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const fetchClaim = async (data: {
  dossierId: string;
  stateDb?: boolean;
}): Promise<Claim> => {
  const url = new URL(`/app/v1/dossiers/${data.dossierId}`, API_BASE_URL);

  const claim = await deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
  if (data.stateDb && !claim.dossier_name && claim.name) {
    claim.dossier_name = claim.name;
  }
  return claim;
};

export const selfServiceCreate = async (data: {
  claimants: Participant[];
  insured?: Participant;
  clientDossierId: string;
  files: SelfServiceClaimFile[];
  assignee?: string;
  notes?: string;
  uploadId?: string;
  dossierType?: string;
}): Promise<string> => {
  const url = new URL(`/app/v1/dossiers`, API_BASE_URL);

  return deserialize(
    await fetchWrapper(url.href, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({
        data: {
          notes: data?.notes,
          claimants: data.claimants,
          insured: data.insured?.name,
        },
        client_dossier_id: data.clientDossierId,
        dossier_name: data.claimants.map((c) => c.name).join(', '),
        files: data.files,
        assignee: data?.assignee,
        upload_id: data?.uploadId,
        // FIXME: backend needs both to create Participants entity
        claimants: data.claimants,
        insured: data.insured,
        dossier_type: data?.dossierType,
      }),
    })
  );
};

export const updateClaim = async (data: {
  dossierId: string;
  status?: string;
  files?: SelfServiceClaimFile[];
  notes?: string;
  uploadId?: string;
}): Promise<string> => {
  const { dossierId, files, status, notes, uploadId } = data;
  const url = new URL(`/app/v1/dossiers/${dossierId}`, API_BASE_URL);

  return deserialize(
    await fetchWrapper(url.href, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-type': 'application/json' },
      body: JSON.stringify({
        status,
        files,
        data: { notes },
        upload_id: uploadId,
      }),
    })
  );
};

export const deleteClaim = async (data: {
  dossierId: string;
}): Promise<string> => {
  const { dossierId } = data;
  const url = new URL(`/app/v1/dossiers/${dossierId}`, API_BASE_URL);

  return deserialize(
    await fetchWrapper(url.href, {
      method: 'DELETE',
      credentials: 'include',
    })
  );
};

export const fetchInsights = async (data: {
  dossierId: string;
  dataVersion?: string;
}) => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossierId}/insights`,
    API_BASE_URL
  );
  url.searchParams.set('data_version', data.dataVersion || '');

  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const fetchSummaries = async (data: {
  dossierId: string;
  dataVersion?: string;
}) => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossierId}/summary_list`,
    API_BASE_URL
  );
  url.searchParams.set('data_version', data.dataVersion || '');

  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const fetchSummaryTemplates = async (data: {
  dossierId: string;
  dataVersion?: string;
}) => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossierId}/summary_templates`,
    API_BASE_URL
  );
  if (data.dataVersion) {
    url.searchParams.set('data_version', data.dataVersion);
  }

  return deserialize(
    await fetchWrapper(url.href, { method: 'GET', credentials: 'include' })
  );
};

export const generateSummariesAndInsights = async (data: {
  dossierId: string;
}) => {
  const url = new URL(
    `/app/v1/dossiers/${data.dossierId}/summaries_and_insights`,
    API_BASE_URL
  );
  return deserialize(
    await fetchWrapper(url.href, { method: 'POST', credentials: 'include' })
  );
};
